import { useState, useMemo, useCallback } from 'react'
import _ from 'lodash'

// Shared staged-state hook for multi-select facet panels.
//
// applied:  { [name]: true|false }  — current applied selections (from model)
// locked:   { [name]: true }        — locked entries that cannot be unchecked
// commit:   (stagedMap) => updatedModel  — produces the next model to write
// model / changeState / search / onClose — wiring to existing search service
//
// Returns: { staged, toggle, reset, apply, hasChanges }

export default function useStagedSelection({ applied, locked, commit, model, changeState, search, onClose }) {
  const [staged, setStaged] = useState(() => ({ ...(applied || {}) }))

  const toggle = useCallback((name) => {
    if (locked && locked[name]) return
    setStaged((prev) => ({ ...prev, [name]: !prev[name] }))
  }, [locked])

  const reset = useCallback(() => {
    const cleared = {}
    if (locked) {
      Object.keys(locked).forEach((k) => { if (locked[k]) cleared[k] = true })
    }
    setStaged(cleared)
  }, [locked])

  const apply = useCallback(() => {
    const updated = _.cloneDeep(model)
    commit(updated, staged)
    updated.filters.pagination.page = 1
    changeState(updated)
    search({ m: updated })
    document.dispatchEvent(new CustomEvent('update-listing-filters', {
      detail: { filters: updated.filters }
    }))
    if (onClose) onClose()
  }, [model, staged, commit, changeState, search, onClose])

  const hasChanges = useMemo(() => {
    const a = applied || {}
    const keys = new Set([...Object.keys(a), ...Object.keys(staged)])
    for (const k of keys) {
      if (!!a[k] !== !!staged[k]) return true
    }
    return false
  }, [applied, staged])

  // Bulk setter for callers that need to update many entries at once
  // (e.g. parent → children propagation in the category tree). Respects
  // locked entries — locked-true values pass through unchanged.
  const setMany = useCallback((updater) => {
    setStaged((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater }
      if (locked) {
        for (const k of Object.keys(locked)) {
          if (locked[k]) next[k] = true
        }
      }
      return next
    })
  }, [locked])

  return { staged, toggle, setMany, reset, apply, hasChanges }
}
